<template>
  <div class="login-page">
    <div class="login-card">
      <div class="login-header">
        <img src="/xoul.png" alt="Xoul" class="login-icon" />
        <h1>{{ isSignup ? $t('auth.signup_title') : $t('auth.login_title') }}</h1>
        <p class="login-subtitle">{{ $t('auth.subtitle') }}</p>
      </div>

      <!-- OAuth Buttons -->
      <div class="oauth-buttons">
        <a :href="googleUrl" class="oauth-btn google-btn">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Google {{ isSignup ? $t('auth.signup') : $t('auth.login') }}
        </a>
        <a :href="facebookUrl" class="oauth-btn facebook-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          Facebook {{ isSignup ? $t('auth.signup') : $t('auth.login') }}
        </a>
      </div>

      <div class="divider">
        <span>{{ $t('auth.or') }}</span>
      </div>

      <!-- Email Form -->
      <form @submit.prevent="handleSubmit" class="email-form">
        <div v-if="isSignup" class="form-group">
          <label>{{ $t('auth.name') }}</label>
          <input v-model="name" type="text" :placeholder="$t('auth.name_placeholder')" class="form-input" />
        </div>
        <div class="form-group">
          <label>{{ $t('auth.email') }}</label>
          <input v-model="email" type="email" :placeholder="$t('auth.email_placeholder')" class="form-input" required />
        </div>
        <div class="form-group">
          <label>{{ $t('auth.password') }}</label>
          <input v-model="password" type="password" :placeholder="$t('auth.password_placeholder')" class="form-input" required minlength="6" />
        </div>

        <p v-if="error" class="error-msg">⚠ {{ error }}</p>

        <button type="submit" class="submit-btn" :disabled="loading">
          {{ loading ? '...' : (isSignup ? $t('auth.signup') : $t('auth.login')) }}
        </button>
      </form>

      <p class="toggle-mode">
        {{ isSignup ? $t('auth.has_account') : $t('auth.no_account') }}
        <a href="#" @click.prevent="isSignup = !isSignup">
          {{ isSignup ? $t('auth.login') : $t('auth.signup') }}
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import { auth } from '../auth'

export default {
  name: 'LoginView',
  data() {
    return {
      isSignup: false,
      email: '',
      password: '',
      name: '',
      error: '',
      loading: false,
    }
  },
  computed: {
    googleUrl() { return auth.getOAuthUrl('google') },
    facebookUrl() { return auth.getOAuthUrl('facebook') },
  },
  mounted() {
    // Check for token in URL (OAuth callback)
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')
    if (token) {
      auth.setAuth(token, null)
      auth.fetchProfile().then(() => {
        this.$router.push('/profile')
      })
    }
    // Already logged in?
    if (auth.isLoggedIn()) {
      this.$router.push('/profile')
    }
  },
  methods: {
    async handleSubmit() {
      this.error = ''
      this.loading = true
      try {
        if (this.isSignup) {
          await auth.signup(this.email, this.password, this.name)
        } else {
          await auth.login(this.email, this.password)
        }
        this.$router.push('/profile')
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.login-page {
  min-height: calc(100vh - 70px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.login-card {
  background: var(--surface0);
  border: 1px solid var(--surface2);
  border-radius: var(--radius);
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  animation: fadeInUp 0.5s ease-out;
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  margin: 0 auto 16px;
  animation: float 3s ease-in-out infinite;
}

.login-header h1 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.login-subtitle {
  color: var(--subtext0);
  font-size: 14px;
}

.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}

.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all var(--transition);
  cursor: pointer;
}

.google-btn {
  background: var(--surface1);
  color: var(--text);
  border: 1px solid var(--surface2);
}
.google-btn:hover {
  background: var(--surface2);
  color: var(--text);
}

.facebook-btn {
  background: #1877F2;
  color: white;
  border: 1px solid #1877F2;
}
.facebook-btn:hover {
  background: #166FE5;
  color: white;
}

.divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  color: var(--overlay1);
  font-size: 12px;
}
.divider::before, .divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--surface2);
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--subtext1);
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: var(--radius-xs);
  border: 1px solid var(--surface2);
  background: var(--mantle);
  color: var(--text);
  font-size: 14px;
  font-family: inherit;
  transition: border-color var(--transition);
}
.form-input:focus {
  outline: none;
  border-color: var(--blue);
}

.error-msg {
  color: var(--red);
  font-size: 13px;
  margin-bottom: 12px;
}

.submit-btn {
  width: 100%;
  padding: 12px;
  border-radius: var(--radius-sm);
  background: linear-gradient(135deg, var(--blue), var(--sapphire));
  color: white;
  font-size: 15px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all var(--transition);
}
.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px var(--accent-glow);
}
.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-mode {
  text-align: center;
  margin-top: 20px;
  font-size: 13px;
  color: var(--subtext0);
}
.toggle-mode a {
  color: var(--blue);
  font-weight: 600;
}
</style>
